/**
 * In-memory holder for the short-lived, single-use ADF lead grant that the
 * streaming AI response mints inline (openai-responses-stream).
 *
 * The token is never persisted (no localStorage/sessionStorage) and is cleared
 * as soon as it is read or expires. When no inline grant is available (non-stream
 * dealers, form submissions outside a stream), adfLeadPush falls back to the grant
 * minted by the get_dealer_adf_info RPC that already fires on the lead-push path.
 */

interface StoredLeadGrant {
  token: string;
  expiresAt: number;
  sessionId: string;
  dealerId: number;
}

let current: StoredLeadGrant | null = null;

export const setLeadGrant = (grant: {
  token?: string;
  expiresAt?: string;
  sessionId?: string;
  dealerId?: number | string;
}): void => {
  if (!grant?.token || !grant.sessionId) return;
  const expiresAt = grant.expiresAt ? new Date(grant.expiresAt).getTime() : Date.now() + 60_000;
  current = {
    token: grant.token,
    expiresAt,
    sessionId: String(grant.sessionId),
    dealerId: Number(grant.dealerId) || 0,
  };
};

/**
 * Returns the token once (single-use) when it matches the session/dealer and is
 * still valid. Always clears any stale grant.
 */
export const consumeLeadGrant = (sessionId: string, dealerId: number): string | null => {
  if (!current) return null;
  const grant = current;
  current = null;

  if (grant.expiresAt <= Date.now() + 2_000) return null;
  if (grant.sessionId !== String(sessionId)) return null;
  if (grant.dealerId && Number(dealerId) && grant.dealerId !== Number(dealerId)) return null;

  return grant.token;
};

export const clearLeadGrant = (): void => {
  current = null;
};
